import * as i0 from '@angular/core';
import { forwardRef, Directive, Input, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { SECONDS, duration } from '@firestitch/date';
import { Subject, fromEvent } from 'rxjs';
import { debounceTime, tap, takeUntil, delay } from 'rxjs/operators';

function parse(value, inputUnit) {
    if (!value) {
        return 0;
    }
    let seconds = 0;
    value
        .split(' ')
        .forEach((chunk) => {
        const matches = chunk.match(/^(\d*\.?\d*)([YyMdhms])?$/);
        let modifier = matches && matches[2];
        if (!modifier) {
            switch (inputUnit) {
                case 'seconds':
                    {
                        modifier = 's';
                    }
                    break;
                case 'minutes':
                    {
                        modifier = 'm';
                    }
                    break;
                case 'hours':
                    {
                        modifier = 'h';
                    }
                    break;
                case 'days':
                    {
                        modifier = 'd';
                    }
                    break;
                case 'months':
                    {
                        modifier = 'M';
                    }
                    break;
                case 'years':
                    {
                        modifier = 'Y';
                    }
                    break;
                default: {
                    throw 'Invalid duration format';
                }
            }
        }
        const factor = {
            Y: SECONDS.YEAR,
            y: SECONDS.YEAR,
            M: SECONDS.MONTH,
            d: SECONDS.DAY,
            h: SECONDS.HOUR,
            m: SECONDS.MINUTE,
            s: 1
        }[modifier];
        seconds += matches[1] * factor;
    });
    return seconds;
}

/**
 * Should cleanup extra symbols in such examples as:
 * '1h 30m'   => '1h 30m'
 * ' 1h 30m'  => '1h 30m'
 * '1h 30m '  => '1h 30m'
 * '1 h 30m'  => '1h 30m'
 * '1h 30 m'  => '1h 30m'
 * '1 h 30 m' => '1h 30m',
 * '1.3 h'    => '1.3h',
 * '1h30m'    => '1h 30m',
 * '1h some text between 30m' => '1h 30m'
 * @param str
 */
function cleanupInput(str) {
    const parts = str
        .trim()
        /**
         * Cleanup step
         *
         * Here we are trying to fix most popular user mistakes i.e.: 1 h 30 m, 1.3 h and etc.
         */
        .replace(/\s/g, '')
        /**
         * Extract
         *
         * For cases like "1h30m" or "1h some text between 30m".
         * Just extract target time values
         */
        .match(/(\d*\.?\d)+[ydhms]/gi);
    if (parts) {
        /**
         * Join extractions into expected string
         */
        return parts.join(' ');
    }
    return '';
}

class FsDurationDirective {
    constructor(_el, _ngZone) {
        this._el = _el;
        this._ngZone = _ngZone;
        this.unit = 'minutes';
        this.inputUnit = 'hours';
        this.suffix = false;
        this.seconds = false;
        this.minutes = false;
        this.hours = false;
        this.days = false;
        this.months = false;
        this.years = false;
        this._destroy$ = new Subject();
        this._onTouched = () => { };
        this._onChange = (value) => { };
    }
    registerOnChange(fn) {
        this._onChange = fn;
    }
    registerOnTouched(fn) {
        this._onTouched = fn;
    }
    ngOnChanges(changes) {
        const changed = Object.keys(changes).map(key => changes[key]).some((change) => {
            return !change.firstChange;
        });
        if (changed) {
            this._format();
        }
    }
    ngOnInit() {
        if (!this.seconds && !this.minutes && !this.hours && !this.days && !this.months && !this.years) {
            this.minutes = true;
            this.hours = true;
        }
        this._listenKeyDown();
        this._listenFocus();
        this._listenBlur();
    }
    writeValue(value) {
        this._model = value;
        this._format();
    }
    ngAfterViewInit() {
        this._format();
    }
    _format() {
        let value = '';
        if (this._model !== null && this._model !== undefined) {
            value = duration(Number(this._model), {
                unit: this.unit,
                suffix: this.suffix,
                seconds: this.seconds,
                minutes: this.minutes,
                hours: this.hours,
                days: this.days,
                months: this.months,
                years: this.years
            });
        }
        this._el.nativeElement.value = value;
    }
    _parseInput() {
        let model = cleanupInput(this._el.nativeElement.value);
        if (!model.length) {
            const parsedValue = parseFloat(this._el.nativeElement.value);
            if (isNaN(parsedValue)) {
                return this._change(null);
            }
            model = parsedValue.toString();
        }
        try {
            let result = parse(model, this.inputUnit);
            if (this.unit === 'minutes') {
                result = result / 60;
            }
            else if (this.unit === 'hours') {
                result = result / 60 / 60;
            }
            else if (this.unit === 'days') {
                result = result / 60 / 60 / 24;
            }
            this._change(Math.round(result));
        }
        catch (e) { }
    }
    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }
    _change(value) {
        this._model = value;
        this._onChange(value);
    }
    _listenKeyDown() {
        this._ngZone.runOutsideAngular(() => {
            fromEvent(this._el.nativeElement, 'keydown')
                .pipe(debounceTime(200), tap(() => {
                this._onTouched();
            }), takeUntil(this._destroy$))
                .subscribe(() => {
                this._ngZone.run(() => {
                    this._parseInput();
                });
            });
        });
    }
    _listenFocus() {
        fromEvent(this._el.nativeElement, 'focus')
            .pipe(delay(50), takeUntil(this._destroy$))
            .subscribe(() => {
            this._el.nativeElement.select();
        });
    }
    _listenBlur() {
        fromEvent(this._el.nativeElement, 'blur')
            .pipe(takeUntil(this._destroy$))
            .subscribe(() => {
            this._parseInput();
            this._format();
        });
    }
}
FsDurationDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsDurationDirective, deps: [{ token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Directive });
FsDurationDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "12.2.16", type: FsDurationDirective, selector: "[fsDuration]", inputs: { unit: "unit", inputUnit: "inputUnit", suffix: "suffix", seconds: "seconds", minutes: "minutes", hours: "hours", days: "days", months: "months", years: "years" }, providers: [{
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => FsDurationDirective),
            multi: true
        }], usesOnChanges: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsDurationDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[fsDuration]',
                    providers: [{
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => FsDurationDirective),
                            multi: true
                        }]
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.NgZone }]; }, propDecorators: { unit: [{
                type: Input
            }], inputUnit: [{
                type: Input
            }], suffix: [{
                type: Input
            }], seconds: [{
                type: Input
            }], minutes: [{
                type: Input
            }], hours: [{
                type: Input
            }], days: [{
                type: Input
            }], months: [{
                type: Input
            }], years: [{
                type: Input
            }] } });

class FsDurationModule {
}
FsDurationModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsDurationModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
FsDurationModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsDurationModule, declarations: [FsDurationDirective], imports: [CommonModule], exports: [FsDurationDirective] });
FsDurationModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsDurationModule, providers: [], imports: [[
            CommonModule,
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsDurationModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                    ],
                    exports: [
                        FsDurationDirective
                    ],
                    entryComponents: [],
                    declarations: [
                        FsDurationDirective
                    ],
                    providers: [],
                }]
        }] });

/*
 * Public API Surface of fs-menu
 */

/**
 * Generated bundle index. Do not edit.
 */

export { FsDurationDirective, FsDurationModule };
